import { useEffect, useState } from "react";
import { Dialog } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Switch } from "@shared/ui/switch";
import { ORIGIN } from "@shared/api";

const KEY = "airel.trusted-domains";

function trusted(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

function trust(host: string) {
  try {
    localStorage.setItem(KEY, JSON.stringify([...trusted(), host]));
  } catch {
    // Хранилище закрыто — спросим в следующий раз, только и всего.
  }
}

/**
 * «Вы покидаете Airel» — как в Discord: ссылка из сообщения на чужой сайт
 * открывается только после вопроса, где виден настоящий адрес. Подпись
 * ссылки бывает какой угодно, а адрес — нет. Домену можно доверять, и тогда
 * его ссылки открываются сразу.
 */
export function LinkGuard() {
  const [asked, setAsked] = useState<URL | null>(null);
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    // Средняя кнопка открывает ссылку событием auxclick — его ловим так же.
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || (event.button !== 0 && event.button !== 1)) return;
      const link = (event.target as HTMLElement | null)?.closest<HTMLAnchorElement>("a[href]");
      if (!link?.closest(".msg, .embed, .forum, .pin-card, .profile-bio")) return;
      let url: URL;
      try {
        url = new URL(link.href);
      } catch {
        return;
      }
      // Свой адрес — не чужой сайт: и страница, и сервер API (в приложении это разные адреса).
      const own = [window.location.origin, ORIGIN ? new URL(ORIGIN).origin : null];
      if (!/^https?:$/.test(url.protocol) || own.includes(url.origin)) return;
      if (trusted().has(url.hostname)) return;
      event.preventDefault();
      setRemember(false);
      setAsked(url);
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("auxclick", onClick, true);
    };
  }, []);

  if (!asked) return null;

  const go = () => {
    if (remember) trust(asked.hostname);
    window.open(asked.href, "_blank", "noopener,noreferrer");
    setAsked(null);
  };

  return (
    <Dialog
      title="Вы покидаете Airel"
      onClose={() => setAsked(null)}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={() => setAsked(null)}>
            Назад
          </Button>
          <Button type="button" onClick={go}>
            Перейти
          </Button>
        </>
      }
    >
      <p className="small muted" style={{ margin: 0 }}>
        Ссылка ведёт сюда — проверьте адрес, прежде чем переходить:
      </p>
      <div className="link-guard-url">
        {asked.protocol}//<b>{asked.hostname}</b>
        {asked.port ? `:${asked.port}` : ""}
        {asked.pathname}
        {asked.search}
        {asked.hash}
      </div>
      <label className="switch-row">
        <div className="grow">
          <div className="switch-title">Доверять {asked.hostname}</div>
          <div className="switch-note">Ссылки на этот сайт будут открываться без вопроса.</div>
        </div>
        <Switch checked={remember} onCheckedChange={setRemember} />
      </label>
    </Dialog>
  );
}
